import {
    Card,
    CardContent,
    Divider,
    Fade,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { ChangeEvent, FunctionComponent, useState } from 'react';
import {
    ConditionLink,
    createEmptyConditionLink,
    createPostImportConditionLinkDTOFromOriginal,
    PostImportConditionLinkDTO,
} from '@/models/conditionLink';
import { LoadingButton } from '@mui/lab';
import { LoadingContainer } from '@/components/Layout/Container';
import { useNavigate } from 'react-router-dom';
import { useAuthentication } from '@/hooks/useAuthentication';
import {
    useLazyGetConditionLinksImportQuery,
    usePostConditionLinkMutation,
    usePostImportedConditionLinkMutation,
} from '@/api/v2/conditionLinks';
import { useNotification } from '@starjack/features/notifications';
import {
    ConditionLinkPersonTypeMapping,
    PersonTypeMap,
} from '@/pages/rulesets/components/ImportedCondtionLinkPersonTypeMapping';

export const NewConditionLinkGeneralData: FunctionComponent = () => {
    const [createMode, setCreateMode] = useState<'new' | 'import'>('new');
    const [baseData, setBaseData] = useState<{ name: string; id: '' }>({
        name: '',
        id: '',
    });
    const [conditionLinkImport, setConditionLinkImport] =
        useState<PostImportConditionLinkDTO>(null);
    const [conditionLinkImportOriginal, setConditionLinkImportOriginal] =
        useState<ConditionLink>(null);
    const [showMissingAsError, setShowMissingAsError] = useState(false);
    const navigate = useNavigate();
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { addNotification } = useNotification();
    const [postConditionLink, { isLoading: conditionLinkLoading }] =
        usePostConditionLinkMutation();
    const [getImport] = useLazyGetConditionLinksImportQuery();
    const [postImportConditionLink] = usePostImportedConditionLinkMutation();

    const validatePersonMapping = () => {
        if (
            createMode !== 'import' ||
            !conditionLinkImport ||
            !conditionLinkImportOriginal
        ) {
            return true;
        }

        const missingMappedPersonTypes = Object.keys(
            conditionLinkImport.personTypesMapping
        ).filter((key) => !conditionLinkImport.personTypesMapping[key]);

        setShowMissingAsError(!!missingMappedPersonTypes.length);

        return !missingMappedPersonTypes.length;
    };

    const createImportConditionLink = () => {
        if (!validatePersonMapping()) {
            return;
        }

        postImportConditionLink({
            liftOperatorId,
            importConditionLink: conditionLinkImport,
        })
            .unwrap()
            .then((importConditionLink) => {
                addNotification({
                    message: 'Regelwerk erfolgreich importiert',
                    type: 'success',
                });

                navigate(
                    `/regelwerke/details?importId=${importConditionLink.id}`
                );
            });
    };

    const onConditionLinkSave = () => {
        if (createMode === 'import') {
            createImportConditionLink();
            return;
        }

        if (baseData.name) {
            postConditionLink({
                liftOperatorId,
                conditionLink: {
                    ...createEmptyConditionLink(),
                    name: baseData.name,
                },
            })
                .unwrap()
                .then((newConditionLink) => {
                    addNotification({
                        message: 'Regelwerk erfolgreich gespeichert',
                        type: 'success',
                    });

                    navigate(`/regelwerke/details?id=${newConditionLink.id}`);
                })
                .catch(() => {
                    addNotification({
                        message:
                            'Fehler beim Speichern. Bitte später noch einmal versuchen',
                        type: 'success',
                    });
                });
        } else {
            setCreateMode('import');
            getImport({
                importId: baseData.id,
            })
                .unwrap()
                .then((conditionLink) => {
                    addNotification({
                        type: 'success',
                        message:
                            'Regelwerk wurde gefunden. Bitte alle Personen ihren Personentypen zuordnen',
                    });

                    setConditionLinkImportOriginal(conditionLink);
                    setConditionLinkImport(
                        createPostImportConditionLinkDTOFromOriginal(
                            conditionLink
                        )
                    );
                })
                .catch((e) => {
                    if (e.status >= 400 && e.status < 500) {
                        addNotification({
                            type: 'error',
                            message:
                                'Fehler beim Importieren des Regelwerks. Bitte die ID überprüfen und noch einmal probieren',
                        });
                    } else if (e.statsu >= 500) {
                        addNotification({
                            type: 'error',
                            message:
                                'Fehler beim Importieren des Regelwerks. Bitte später noch einmal probieren',
                        });
                    }
                });
        }
    };

    const updateConditionLink =
        (key: 'name' | 'id') => (e: ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            setBaseData((prev) => ({
                ...prev,
                [key]: value,
            }));
        };

    const updateImportConditionLinkPersonMap = (
        personTypeMap: PersonTypeMap
    ) => {
        setConditionLinkImport((prev) => ({
            ...prev,
            personTypesMapping: {
                ...personTypeMap,
            },
        }));
    };

    return (
        <Stack spacing={2}>
            <Typography
                sx={{
                    width: '75%',
                }}
            >
                Hier lassen sich allgemeine Daten für das Regelwerk festlegen.
                Bitte beachten: Um eine Kontingent- oder Preisregel zu
                erstellen, muss zuerst ein Name für das Regelwerk angegeben und
                gespeichert werden.
            </Typography>

            <Card
                sx={{
                    position: 'relative',
                }}
            >
                <CardContent
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant='h6'>Allgemeine Daten</Typography>

                    <LoadingButton
                        sx={{
                            ml: 'auto',
                        }}
                        onClick={onConditionLinkSave}
                        variant='contained'
                        loading={conditionLinkLoading}
                        disabled={!baseData.name && !baseData.id}
                    >
                        {createMode == 'import'
                            ? 'Regelwerk importieren'
                            : 'Speichern'}
                    </LoadingButton>
                </CardContent>

                <Divider
                    sx={{
                        width: '100%',
                    }}
                />

                <CardContent>
                    <Stack
                        sx={{
                            mb: 6,
                        }}
                        spacing={2}
                    >
                        <TextField
                            sx={{
                                width: 400,
                            }}
                            value={baseData.name}
                            onChange={updateConditionLink('name')}
                            label='Name'
                            disabled={!!baseData.id}
                        />
                    </Stack>

                    {!!conditionLinkImport && (
                        <ConditionLinkPersonTypeMapping
                            onMappingChange={updateImportConditionLinkPersonMap}
                            personTypes={
                                conditionLinkImportOriginal.personTypes
                            }
                            personsToMap={
                                conditionLinkImport.personTypesMapping
                            }
                            showMissingWithError={showMissingAsError}
                        />
                    )}
                </CardContent>

                <Fade in={false}>
                    <div>
                        <LoadingContainer cover />
                    </div>
                </Fade>
            </Card>
        </Stack>
    );
};
